import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { FileBarChart, FileText, FileSpreadsheet, Eye, Search, Loader2, Lock, RotateCcw, ArrowUp, ArrowDown, Filter } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import EmployeeProfileCard from '@/components/personnel/EmployeeProfileCard';
import DataSheetViewer from '@/components/personnel/DataSheetViewer';
import { Download, IdCard } from 'lucide-react';

const parseFilename = (disposition, fallback) => {
  if (!disposition) return fallback;
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return m ? decodeURIComponent(m[1]) : fallback;
};

const SOURCE_LABELS = { customers: 'Customers', service_locations: 'Service Locations', personnel: 'Personnel', assignments: 'Assignments', vehicles: 'Vehicles' };
const DETAIL_LABELS = { summary: 'Summary', contact: 'Contact & Address', assignments: 'Assignments & Roles', full: 'Full Authorized Record' };
const SEL_LABELS = { all: 'All authorized records', filtered: 'Current filtered results', selected: 'Selected records' };

const DEFAULTS = { source: 'personnel', detail: 'summary', selMode: 'all', exclude: [], checked: [], search: '', sort: null, colFilters: {} };
const LS_KEY = (uid) => `tfs_report_builder_v1_${uid || 'anon'}`;

const ReportsView = () => {
  const { token, user } = useContext(AuthContext);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [meta, setMeta] = useState(null);
  const [source, setSource] = useState(DEFAULTS.source);
  const [detail, setDetail] = useState(DEFAULTS.detail);
  const [selMode, setSelMode] = useState(DEFAULTS.selMode);
  const [records, setRecords] = useState({ headers: [], rows: [], ids: [] });
  const [search, setSearch] = useState('');
  const [checked, setChecked] = useState(new Set());
  const [exclude, setExclude] = useState(new Set());
  const [sort, setSort] = useState(null);              // { column, dir }
  const [colFilters, setColFilters] = useState({});    // { header: text }
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [mode, setMode] = useState('builder');           // 'builder' | 'datasheet'
  const [people, setPeople] = useState(null);
  const [dsSearch, setDsSearch] = useState('');
  const [dsSelected, setDsSelected] = useState(null);
  const [dsDownloading, setDsDownloading] = useState(false);
  const [dsView, setDsView] = useState('card');          // 'card' | 'pdf'
  const [dsBulk, setDsBulk] = useState(false);
  const hydrated = useRef(false);
  const sourceChanged = useRef(false);
  const shouldAutoPreview = useRef(false);

  useEffect(() => { axios.get(`${API}/admin-reports/meta`, auth).then((r) => setMeta(r.data)).catch(() => toast.error('No reporting access')); }, [token]);

  useEffect(() => {
    if (mode !== 'datasheet' || people) return;
    axios.get(`${API}/personnel?status_filter=all`, auth)
      .then((r) => setPeople(Array.isArray(r.data) ? r.data : (r.data.personnel || r.data.items || [])))
      .catch(() => setPeople([]));
  }, [mode, people, token]);

  const downloadDataSheet = async (pid, pnum) => {
    setDsDownloading(true);
    try {
      const r = await axios.get(`${API}/personnel/${pid}/summary.pdf`, { ...auth, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = parseFilename(r.headers?.['content-disposition'], `TFS_Personnel_DataSheet_${pnum || pid}.pdf`);
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not download the data sheet'); }
    setDsDownloading(false);
  };

  const dsFiltered = (people || []).filter((p) => !dsSearch
    || (p.name || '').toLowerCase().includes(dsSearch.toLowerCase()) || String(p.personnel_number).includes(dsSearch));

  const downloadAllDataSheets = async () => {
    if (!dsFiltered.length) return;
    setDsBulk(true);
    try {
      const r = await axios.post(`${API}/personnel/summary-bulk.pdf`, { ids: dsFiltered.map((p) => p.id) },
        { ...auth, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `TFS_Personnel_DataSheets_${dsFiltered.length}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk download failed'); }
    setDsBulk(false);
  };

  // ---- Section 7: hydrate the retained working report once meta is available ----
  useEffect(() => {
    if (!meta || hydrated.current) return;
    try {
      const raw = localStorage.getItem(LS_KEY(user?.id));
      if (raw) {
        const s = JSON.parse(raw);
        if (s.source && meta.sources.includes(s.source)) setSource(s.source);
        if (s.detail) setDetail(s.detail);
        if (s.selMode) setSelMode(s.selMode);
        if (Array.isArray(s.exclude)) setExclude(new Set(s.exclude));
        if (Array.isArray(s.checked)) setChecked(new Set(s.checked));
        if (typeof s.search === 'string') setSearch(s.search);
        if (s.sort) setSort(s.sort);
        if (s.colFilters) setColFilters(s.colFilters);
        if (s.hadPreview) shouldAutoPreview.current = true; // Section 7: auto-restore the open preview
      }
    } catch (e) { /* ignore malformed */ }
    hydrated.current = true;
  }, [meta, user]);

  // ---- Section 7: persist config + selected ids (no sensitive report data) ----
  useEffect(() => {
    if (!hydrated.current) return;
    const snap = { source, detail, selMode, exclude: [...exclude], checked: [...checked], search, sort, colFilters, hadPreview: !!preview };
    try { localStorage.setItem(LS_KEY(user?.id), JSON.stringify(snap)); } catch (e) { /* quota */ }
  }, [source, detail, selMode, exclude, checked, search, sort, colFilters, preview, user]);

  // ---- Section 7: rebuild the SAME preview from saved config, no manual Preview click ----
  useEffect(() => {
    if (!hydrated.current || !shouldAutoPreview.current) return;
    if (!records.ids && !records.rows?.length) return; // wait until the source's records are loaded
    shouldAutoPreview.current = false;
    runPreview();
  }, [records]);

  // Load the authorized record list (summary) for the current source, for the picker.
  const loadRecords = useCallback(async (src) => {
    try {
      const r = await axios.post(`${API}/admin-reports/preview`,
        { source: src, detail_level: 'summary', selection: { mode: 'all' } }, auth);
      setRecords({ headers: r.data.headers, rows: r.data.rows, ids: r.data.ids });
    } catch (e) { setRecords({ headers: [], rows: [], ids: [] }); }
  }, [token]);

  useEffect(() => {
    if (!meta) return;
    loadRecords(source);
    if (sourceChanged.current) {   // reset selections/sort only on a genuine source switch, not on hydrate
      setChecked(new Set()); setExclude(new Set()); setSort(null); setColFilters({});
    }
    setPreview(null);
    sourceChanged.current = true;
  }, [source, meta, loadRecords]);

  const filteredIdx = records.rows
    .map((row, i) => ({ row, i }))
    .filter(({ row }) => !search || row.join(' ').toLowerCase().includes(search.toLowerCase()));

  const buildSelection = () => {
    if (selMode === 'all') return { mode: 'all' };
    if (selMode === 'filtered') return { mode: 'selected', ids: filteredIdx.map(({ i }) => records.ids[i]).filter(Boolean) };
    const ids = [...checked];
    return { mode: ids.length === 1 ? 'one' : 'selected', ids };
  };

  const body = () => ({ source, detail_level: detail, selection: buildSelection(),
    exclude_fields: [...exclude], sort, column_filters: colFilters });

  const runPreview = useCallback(async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin-reports/preview`, body(), auth);
      setPreview(r.data);
      if (source === 'personnel' && detail === 'full' && r.data.detail_level !== 'full')
        toast.info('Full personnel detail requires the reports.personnel_full permission — showing Contact detail.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
    setBusy(false);
  }, [source, detail, selMode, exclude, checked, search, sort, colFilters, token]);

  // Section 6: re-run preview automatically when sort / column filters change so
  // the on-screen preview always reflects what an export will produce.
  useEffect(() => {
    if (!preview) return;
    const t = setTimeout(() => { runPreview(); }, 300);
    return () => clearTimeout(t);
  }, [sort, colFilters]);

  const runExport = async (fmt) => {
    setExporting(fmt);
    try {
      const r = await axios.post(`${API}/admin-reports/export`, { ...body(), format: fmt },
        { ...auth, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `tfs_${source}_report.${fmt === 'xlsx' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
    setExporting('');
  };

  const cycleSort = (header) => {
    setSort((prev) => {
      if (!prev || prev.column !== header) return { column: header, dir: 'asc' };
      if (prev.dir === 'asc') return { column: header, dir: 'desc' };
      return null; // third click clears
    });
  };

  const hasMeaningful = source !== DEFAULTS.source || detail !== DEFAULTS.detail || selMode !== DEFAULTS.selMode
    || exclude.size > 0 || checked.size > 0 || !!search || !!sort || Object.keys(colFilters).length > 0;

  const doReset = () => {
    setSource(DEFAULTS.source); setDetail(DEFAULTS.detail); setSelMode(DEFAULTS.selMode);
    setExclude(new Set()); setChecked(new Set()); setSearch(''); setSort(null); setColFilters({});
    setPreview(null); sourceChanged.current = false;
    try { localStorage.removeItem(LS_KEY(user?.id)); } catch (e) { /* noop */ }
    setResetOpen(false);
    toast.success('Report reset to default');
  };

  const onResetClick = () => { hasMeaningful ? setResetOpen(true) : doReset(); };

  if (!meta) return <div className="p-8 text-slate-500">Loading reports…</div>;
  const availableHeaders = (meta.fields[source] || {})[detail] || [];
  const inputCls = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm';

  return (
    <div data-testid="reports-view">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm font-bold uppercase tracking-[0.18em] text-[#8FC93A]">Administration Hub</div>
          <h1 className="mt-1.5 flex items-center gap-2 text-[30px] font-extrabold tracking-tight text-[#0E2A47]"><FileBarChart size={26} /> Reports</h1>
          <p className="mt-2 max-w-2xl text-base text-slate-500">Build read-only, TFS-branded reports. Sort and filter columns to shape the result; Preview and exports stay in sync. Respects your access scope; exports never include sensitive personnel, banking or payroll data.</p>
        </div>
        <button onClick={onResetClick} data-testid="report-reset-btn"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
          <RotateCcw size={15} /> Reset Report
        </button>
      </div>

      <div className="mb-5 inline-flex rounded-xl border border-slate-200 bg-white p-1" data-testid="report-mode-toggle">
        <button onClick={() => setMode('builder')} data-testid="report-mode-builder"
          className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold ${mode === 'builder' ? 'bg-[#0E2A47] text-white' : 'text-[#0E2A47] hover:bg-slate-100'}`}>
          <FileBarChart size={15} /> Record Reports
        </button>
        <button onClick={() => setMode('datasheet')} data-testid="report-mode-datasheet"
          className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold ${mode === 'datasheet' ? 'bg-[#0E2A47] text-white' : 'text-[#0E2A47] hover:bg-slate-100'}`}>
          <IdCard size={15} /> Personnel Data Sheet
        </button>
      </div>

      {mode === 'datasheet' ? (
        <div className="grid gap-5 lg:grid-cols-[340px_1fr]" data-testid="datasheet-view">
          <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-bold uppercase tracking-wide text-[#8FC93A]">Select an employee</span>
              <button onClick={downloadAllDataSheets} disabled={dsBulk || !dsFiltered.length}
                data-testid="datasheet-download-all"
                className="inline-flex items-center gap-1 rounded-lg border border-[#0E2A47] px-2.5 py-1.5 text-xs font-semibold text-[#0E2A47] hover:bg-slate-100 disabled:opacity-50">
                {dsBulk ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Download all ({dsFiltered.length})
              </button>
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
              <Search size={15} className="text-slate-400" />
              <input value={dsSearch} onChange={(e) => setDsSearch(e.target.value)} placeholder="Search by name or #"
                className="w-full text-sm outline-none" data-testid="datasheet-search" />
            </div>
            <div className="max-h-[60vh] overflow-auto">
              {people === null ? <div className="py-10 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>
                : (dsFiltered.map((p) => (
                      <button key={p.id} onClick={() => setDsSelected(p)} data-testid={`datasheet-pick-${p.id}`}
                        className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm ${dsSelected?.id === p.id ? 'bg-[#0E2A47] text-white' : 'hover:bg-slate-100 text-slate-700'}`}>
                        <span className="font-mono text-xs opacity-70">#{p.personnel_number}</span>
                        <span className="truncate font-medium">{p.name}</span>
                      </button>)))}
              {people && dsFiltered.length === 0 && <div className="py-10 text-center text-sm text-slate-400">No personnel found.</div>}
            </div>
          </div>
          <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4" data-testid="datasheet-preview">
            {!dsSelected ? (
              <div className="py-20 text-center text-sm text-slate-400">Select an employee to preview their full data sheet, then download the TFS-branded PDF. Use “Download all” to export the whole filtered group at once.</div>
            ) : (
              <>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-[#0E2A47]">Preview — {dsSelected.name}</span>
                  <div className="flex items-center gap-2">
                    <div className="inline-flex rounded-lg border border-slate-200 p-0.5" data-testid="datasheet-view-toggle">
                      <button onClick={() => setDsView('card')} data-testid="datasheet-view-card"
                        className={`rounded px-2.5 py-1 text-xs font-semibold ${dsView === 'card' ? 'bg-[#0E2A47] text-white' : 'text-[#0E2A47]'}`}>On-screen card</button>
                      <button onClick={() => setDsView('pdf')} data-testid="datasheet-view-pdf"
                        className={`rounded px-2.5 py-1 text-xs font-semibold ${dsView === 'pdf' ? 'bg-[#0E2A47] text-white' : 'text-[#0E2A47]'}`}>PDF preview</button>
                    </div>
                    {dsView === 'card' && (
                      <button onClick={() => downloadDataSheet(dsSelected.id, dsSelected.personnel_number)} disabled={dsDownloading}
                        data-testid="datasheet-download" className="inline-flex items-center gap-1.5 rounded-lg bg-[#5FA718] px-4 py-2 text-sm font-semibold text-white hover:bg-[#4e8f12] disabled:opacity-60">
                        {dsDownloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download PDF
                      </button>
                    )}
                  </div>
                </div>
                {dsView === 'pdf' ? (
                  <DataSheetViewer key={dsSelected.id} personnelId={dsSelected.id} name={dsSelected.name} heightClass="h-[70vh]" showTitle={false} />
                ) : (
                  <EmployeeProfileCard key={dsSelected.id} personnelId={dsSelected.id} />
                )}
              </>
            )}
          </div>
        </div>
      ) : (
      <div className="grid gap-5 lg:grid-cols-[340px_1fr]">
        {/* Builder */}
        <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5">
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-[#8FC93A]">Report Source</label>
            <select className={`${inputCls} mt-1`} value={source} onChange={(e) => setSource(e.target.value)} data-testid="report-source">
              {meta.sources.map((s) => <option key={s} value={s}>{SOURCE_LABELS[s]}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-[#8FC93A]">Detail Level</label>
            <select className={`${inputCls} mt-1`} value={detail} onChange={(e) => setDetail(e.target.value)} data-testid="report-detail">
              {meta.detail_levels.map((d) => <option key={d} value={d}>{DETAIL_LABELS[d]}</option>)}
            </select>
            {source === 'personnel' && detail === 'full' && !meta.can_personnel_full && (
              <p className="mt-1 flex items-center gap-1 text-[11px] text-amber-700"><Lock size={12} /> You lack full-personnel permission — sensitive fields will be withheld.</p>
            )}
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-[#8FC93A]">Records</label>
            <div className="mt-1 space-y-1">
              {['all', 'filtered', 'selected'].map((m) => (
                <label key={m} className="flex items-center gap-2 text-sm text-slate-600">
                  <input type="radio" name="selmode" checked={selMode === m} onChange={() => setSelMode(m)} data-testid={`report-selmode-${m}`} />
                  {SEL_LABELS[m]}{m === 'selected' && checked.size > 0 && <span className="text-xs text-slate-400">({checked.size})</span>}
                </label>
              ))}
            </div>
          </div>
          {availableHeaders.length > 0 && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-[#8FC93A]">Include Fields</label>
              <div className="mt-1 flex flex-wrap gap-1.5" data-testid="report-fields">
                {availableHeaders.map((h) => {
                  const on = !exclude.has(h);
                  return (
                    <button key={h} type="button" onClick={() => setExclude((s) => { const n = new Set(s); on ? n.add(h) : n.delete(h); return n; })}
                      className={`rounded-full px-2.5 py-1 text-[11px] font-medium border ${on ? 'bg-[#0E2A47] text-white border-[#0E2A47]' : 'bg-white text-slate-400 border-gray-200 line-through'}`}>{h}</button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <button onClick={runPreview} disabled={busy} data-testid="report-preview-btn"
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#0E2A47] px-3 py-2 text-sm font-semibold text-white hover:bg-[#16324F] disabled:opacity-60">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />} Preview
            </button>
          </div>
          <div className="flex gap-2">
            <button onClick={() => runExport('pdf')} disabled={!!exporting} data-testid="report-export-pdf"
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-[#0E2A47] hover:bg-slate-50">
              {exporting === 'pdf' ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />} PDF
            </button>
            <button onClick={() => runExport('xlsx')} disabled={!!exporting} data-testid="report-export-xlsx"
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-[#5FA718] hover:bg-slate-50">
              {exporting === 'xlsx' ? <Loader2 size={15} className="animate-spin" /> : <FileSpreadsheet size={15} />} Excel
            </button>
          </div>
        </div>

        {/* Record picker + preview */}
        <div className="min-w-0 space-y-4">
          {selMode === 'selected' && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="report-record-picker">
              <div className="flex items-center gap-2 border-b border-gray-100 pb-2">
                <Search size={15} className="text-slate-400" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search records…"
                  className="w-full text-sm outline-none" data-testid="report-record-search" />
                <span className="text-xs text-slate-400">{filteredIdx.length} shown</span>
              </div>
              <div className="mt-2 max-h-64 overflow-auto">
                {filteredIdx.map(({ row, i }) => {
                  const id = records.ids[i];
                  const on = checked.has(id);
                  return (
                    <label key={id} className="flex items-center gap-2 border-b border-gray-50 py-1.5 text-sm text-slate-600">
                      <input type="checkbox" checked={on} onChange={() => setChecked((s) => { const n = new Set(s); on ? n.delete(id) : n.add(id); return n; })} />
                      <span className="truncate">{row.slice(0, 3).join(' · ')}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4" data-testid="report-preview-panel">
            {!preview ? (
              <div className="py-16 text-center text-sm text-slate-400">Configure a report and click <b>Preview</b>.</div>
            ) : (
              <>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-semibold text-[#0E2A47]">{SOURCE_LABELS[preview.source]} — {DETAIL_LABELS[preview.detail_level]}</span>
                  <span className="text-xs text-slate-400" data-testid="report-preview-count">{preview.total} record(s){preview.truncated ? ' · showing first 200' : ''}</span>
                </div>
                <div className="overflow-auto max-h-[52vh] rounded-lg border border-gray-100">
                  <table className="min-w-full text-xs">
                    <thead className="bg-[#0E2A47] text-white sticky top-0">
                      <tr>
                        {preview.headers.map((h) => {
                          const active = sort?.column === h;
                          return (
                            <th key={h} className="px-2.5 py-2 text-left font-semibold whitespace-nowrap">
                              <button type="button" onClick={() => cycleSort(h)} data-testid={`report-sort-${h.replace(/[^a-zA-Z0-9]+/g, '-')}`}
                                className="inline-flex items-center gap-1 hover:text-[#8FC93A]">
                                {h}
                                {active && (sort.dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
                              </button>
                            </th>
                          );
                        })}
                      </tr>
                      <tr className="bg-[#16324F]">
                        {preview.headers.map((h) => (
                          <th key={h} className="px-1.5 pb-1.5 pt-1">
                            <div className="flex items-center gap-1 rounded bg-white/95 px-1.5">
                              <Filter size={10} className="text-slate-400" />
                              <input value={colFilters[h] || ''} onChange={(e) => setColFilters((f) => {
                                  const n = { ...f }; const v = e.target.value; if (v) n[h] = v; else delete n[h]; return n; })}
                                data-testid={`report-colfilter-${h.replace(/[^a-zA-Z0-9]+/g, '-')}`}
                                placeholder="Filter" className="w-full min-w-[60px] bg-transparent py-0.5 text-[11px] font-normal text-slate-700 outline-none" />
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((r, ri) => (
                        <tr key={ri} className={ri % 2 ? 'bg-slate-50' : 'bg-white'}>
                          {r.map((c, ci) => <td key={ci} className="px-2.5 py-1.5 whitespace-nowrap text-slate-700">{String(c ?? '')}</td>)}
                        </tr>
                      ))}
                      {preview.rows.length === 0 && <tr><td colSpan={preview.headers.length || 1} className="px-3 py-8 text-center text-slate-400">No records for this selection / your access scope.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      )}

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="max-w-md" data-testid="report-reset-dialog">
          <DialogHeader><DialogTitle>Reset Report?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">This will discard your current report configuration — source, detail, selected records, included fields, filters and sorting — and restore the default report.</p>
          <DialogFooter>
            <button onClick={() => setResetOpen(false)} className="rounded-md border border-gray-200 px-4 py-2 text-sm" data-testid="report-reset-cancel">Keep My Report</button>
            <button onClick={doReset} className="rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F]" data-testid="report-reset-confirm">Reset Report</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportsView;
